import TireInstallationAPI from 'api/tire-installation.api';
import { BaseHandler } from './base.handler';
import {
  setTireInstallations,
  setIsLoading,
  setIsLoaded,
} from 'store/tire-installation.slice';
import { showNotification } from 'store/notification.slice';
import {
  type TireInstallationQueryParams,
  type PerformanceLogQueryParams,
  type DownloadImageParams,
  type PerformanceLogDetail,
  type CreatePerformanceLogPayload,
} from 'types/tire-installation.types';
import { type UninstallTireRequest } from 'types/tire-inventory.types';

export default class TireInstallationHandler extends BaseHandler {
  private readonly api: TireInstallationAPI;

  constructor() {
    super();
    this.api = new TireInstallationAPI();
  }

  /**
   * Gets active tire installations
   */
  async getActiveTireInstallations(
    params: TireInstallationQueryParams
  ): Promise<void> {
    this.dispatch(setIsLoading(true));
    this.dispatch(setIsLoaded(false));

    try {
      const response = await this.api.getActiveTireInstallations(params);
      this.dispatch(setTireInstallations(response));
      this.dispatch(setIsLoaded(true));
      this.handleSuccess();
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.handleError(
        'An error occurred while retrieving tire installations: ' +
          (msg || String(error?.message || 'Unknown error'))
      );
    } finally {
      this.dispatch(setIsLoading(false));
    }
  }

  /**
   * Gets performance logs for a tire installation
   */
  async getPerformanceLogs(
    params: PerformanceLogQueryParams
  ): Promise<PerformanceLogDetail[]> {
    try {
      const response = await this.api.getPerformanceLogs(params);
      return response;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.handleError(
        'An error occurred while retrieving performance logs: ' +
          (msg || String(error?.message || 'Unknown error'))
      );
      return [];
    }
  }

  /**
   * Gets download URL for performance log image and opens it
   */
  async downloadPerformanceLogImage(
    params: DownloadImageParams
  ): Promise<void> {
    try {
      const response = await this.api.getPerformanceLogImageUrl(params);
      // Handle both string URL and object response
      const downloadUrl =
        typeof response === 'string'
          ? response
          : response?.url || response?.downloadUrl;

      if (downloadUrl) {
        // Open the download URL in a new tab
        window.open(downloadUrl, '_blank');
      } else {
        console.error('No download URL found in response:', response);
        this.handleError('No download URL found in response');
      }
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.handleError(
        'An error occurred while downloading the image: ' +
          (msg || String(error?.message || 'Unknown error'))
      );
    }
  }

  /**
   * Creates a new performance log entry
   */
  async createPerformanceLog(
    payload: CreatePerformanceLogPayload
  ): Promise<boolean> {
    try {
      await this.api.createPerformanceLog(payload);
      this.dispatch(
        showNotification({
          message: 'Inspection created successfully',
          type: 'success',
        })
      );
      return true;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        // Priority: message field contains detailed validation errors
        msg = error.response?.data?.message as string;

        // Fallback to error field if message is not available
        if (!msg && error.response?.data?.error) {
          msg = error.response?.data?.error as string;
        }
      }

      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ??
                'An error occurred while creating the inspection'
            ),
          type: 'error',
        })
      );
      return false;
    }
  }

  /**
   * Uninstalls a tire from an asset
   */
  async uninstallTire(
    payload: UninstallTireRequest,
    customerId: string
  ): Promise<boolean> {
    try {
      await this.api.uninstallTire(payload, customerId);
      this.dispatch(
        showNotification({
          message: 'Tire uninstalled successfully',
          type: 'success',
        })
      );
      return true;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ?? 'An error occurred while uninstalling the tire'
            ),
          type: 'error',
        })
      );
      return false;
    }
  }
}
